<script lang="ts">
    import {datatypes, type Rec} from 'src/typings';
    import {getCurrentLanguage, getLocalization} from 'src/core';
    import type {NextAcceptableValues, HierarchicalRecordRow, SimpleGridFieldValue} from '../../../lib/types';
    import type {Row} from '@tanstack/svelte-table';
    import type {ForceSetting} from 'shared/value-editors/kp-value-editor-force-settings/types';
    import type {ModelInputChangeEvent} from 'shared/value-editors/events';
    import type {SvelteComponentConstructor} from 'core/types';
    import type {EditorValueToGridFieldValueConversionParams} from 'src/features/record-grid/components/cells/data-cell/types';
    import type {FieldTypeDefinition} from 'src/features/record/field-types/types';
    import type {PlainTextParsingContext} from 'src/features/record-grid/lib/cell-serialization-and-deserialization';
    import {parseStringIntoSimpleGridFieldValue} from 'src/features/record-grid/lib/cell-serialization-and-deserialization';
    import type {Subscription} from 'rxjs';
    import type {CellCoordinates, GridCommand, GridState} from 'shared/ui-widgets/grid/types';
    import {ValueEditorSize} from 'shared/value-editors/types';
    import {cleanup, exists, isNullOrUndefined} from 'shared/utils/custom-utils';
    import {createEventDispatcher, onDestroy, onMount} from 'svelte';
    import {getGridContext} from 'shared/ui-widgets/grid/grid.context';
    import {getGridCellContext} from 'shared/ui-widgets/grid/grid-cell/grid-cell.context';
    import {isCellInEditMode} from 'shared/ui-widgets/grid/state/state-query';
    import {hasOriginRecordReference, isErrorGridFieldValue, isOptionGridFieldValue} from 'src/features/record-grid/lib/types-utils';
    import {convertEditorModelToGridFieldValue, convertGridFieldValueToEditorModel} from 'src/features/record-grid/components/cells/data-cell/model-conversion';
    import {isDateOrNumericDatatype} from 'src/features/record-grid/components/cells/data-cell/utils';
    import {createRecordIdFieldTypeId} from 'shared/utils/record-field-utils';
    import {asSupported} from 'src/features/record-grid/lib/grid-value-editors';
    import {fade} from 'svelte/transition';
    import {isFieldValueEqual} from 'src/features/record-grid/lib/grid-fields';
    import OptionFieldValue from './values/OptionFieldValue.svelte';
    import KpValueEditor from 'shared/value-editors/kp-value-editor/KpValueEditor.svelte';
    import ScalarFieldValue from './values/ScalarFieldValue.svelte';
    import ReferenceFieldValue from './values/ReferenceFieldValue.svelte';
    import KpValueEditorForceSettings from 'shared/value-editors/kp-value-editor-force-settings/KpValueEditorForceSettings.svelte';
    import RecordAsChipTagModel from 'shared/value-editors/internal/editors/search-or-edit/templates/RecordAsChipTagModel.svelte';
    import ErrorFieldValue from './values/ErrorFieldValue.svelte';
    import {getOptionId, getOptionIdResolver} from 'shared/value-editors/internal/editors/acceptable-root/utils';

    export let value: SimpleGridFieldValue;
    export let fieldType: FieldTypeDefinition;
    export let row: Row<HierarchicalRecordRow>;
    export let editable = false;

    const localize = getLocalization();
    const locale = getCurrentLanguage();
    const dispatch = createEventDispatcher<{ 'cell-edit': SimpleGridFieldValue | null }>();
    const fadeInAnimParams = {duration: 250};
    const savableEditors = ['date', 'editable-record-search', 'single-acceptable', 'inline-record-search'];
    const forcedEditorSettings: ForceSetting[] = [
        {
            type: 'editable-record-search',
            strategy: 'merge',
            options: {
                modelComponent: RecordAsChipTagModel as SvelteComponentConstructor<{ value: Rec }>,
                searchParams: {constraintsRecordFieldType: createRecordIdFieldTypeId(row.original.id, fieldType.id)}
            }
        },
        {
            type: 'inline-record-search',
            strategy: 'merge',
            options: {
                searchParams: {constraintsRecordFieldType: createRecordIdFieldTypeId(row.original.id, fieldType.id)}
            }
        },
        {
            type: 'single-acceptable',
            strategy: 'merge',
            options: {
                optionDisabledResolver: ({item, model, options}) => {
                    const optionId: string | number = getOptionId(item, getOptionIdResolver(options));
                    const nextAcceptableValues = model?.nextAcceptableValues;
                    return nextAcceptableValues ? !isNextValueAcceptable(nextAcceptableValues, optionId) : false;
                }
            }
        }
    ];

    function isNextValueAcceptable(allowedNextValues: NextAcceptableValues, currentValue: string | number): boolean {
        const {included, excluded} = allowedNextValues;

        if (Array.isArray(included)) {
            return included.includes(currentValue);
        }

        if (Array.isArray(excluded)) {
            return !excluded.includes(currentValue);
        }

        return true;
    }

    const {stateManager, stateManager: {state$}, command$} = getGridContext();
    const {coordinates$} = getGridCellContext();

    let editValue: SimpleGridFieldValue;
    let isInEditMode = false;
    let subscription: Subscription;

    onMount(() => {
        subscription = command$.subscribe(handleCellEditRollbackEvent);
    });

    onDestroy(() => cleanup(subscription));

    $: handleStateUpdate($state$, $coordinates$);

    const handleCellEditRollbackEvent = (command: GridCommand) => {
        if (command.type === 'rollback-cell-change' && command.target.isEqualTo($coordinates$)) {
            rollbackValue();
        }
    };

    const handleModelChange = (event: ModelInputChangeEvent<any>) => {
        if (isNullOrUndefined(event.detail)) {
            editValue = null;
        } else {
            // assume that values and editor types correlates, possible TODO: type check
            const params = {
                editorType: asSupported(fieldType.editor.defaultValue.type),
                currentFieldValue: editValue,
                editorModelValue: event.detail
            } as EditorValueToGridFieldValueConversionParams;
            editValue = convertEditorModelToGridFieldValue(params, localize);
        }

        if (savableEditors.includes(fieldType.editor.defaultValue.type)) {
            stateManager.forceExitEditMode();
        }
    };

    function handleStateUpdate(state: GridState, coordinates: CellCoordinates) {
        if (isCellInEditMode(state, coordinates) && !isInEditMode) {
            handleEditModeEnter(state.edit.initialInput);
        }

        if (!isCellInEditMode(state, coordinates) && isInEditMode) {
            handleEditModeExit();
        }
    }

    function handleEditModeEnter(initialInput?: string) {
        if (!editable) {
            return;
        }

        if (isNullOrUndefined(initialInput)) {
            editValue = value;
        } else {
            const ctx: PlainTextParsingContext = {
                valueDatatype: fieldType.valueDatatype,
                editorType: asSupported(fieldType.editor.defaultValue.type),
                localize,
                locale
            };

            editValue = parseStringIntoSimpleGridFieldValue(initialInput, ctx);
        }

        isInEditMode = true;
    }

    function handleEditModeExit() {
        if (!isFieldValueEqual(value, editValue)) {
            value = editValue;
            saveNewCellValue(value);
        }
        isInEditMode = false;
        editValue = null;
    }

    function rollbackValue() {
        if (!isInEditMode) {
            return;
        }
        if (isFieldValueEqual(value, editValue)) {
            return;
        }
        editValue = value;
    }

    function saveNewCellValue(newValue: SimpleGridFieldValue) {
        dispatch('cell-edit', newValue);
    }
</script>

{#if exists(value)}
    <div class="value-container"
         class:editable-value={editable}
         class:end-align={isDateOrNumericDatatype(fieldType.valueDatatype)}>

        {#if hasOriginRecordReference(value) && fieldType.valueDatatype?.name === datatypes.TEXT}
            <ReferenceFieldValue value={value}/>
        {:else if isErrorGridFieldValue(value)}
            <ErrorFieldValue value={value}/>
        {:else if isOptionGridFieldValue(value)}
            <OptionFieldValue value={value}/>
        {:else}
            <ScalarFieldValue value={value}
                              valueFormat={fieldType.valueFormat}
                              datatype={fieldType.valueDatatype}/>
        {/if}
    </div>
{/if}

{#if exists(fieldType.editor.defaultValue) && isInEditMode}
    <div class="value-editor-container" in:fade={fadeInAnimParams}>
        <KpValueEditorForceSettings forceSettings={forcedEditorSettings}>
            <KpValueEditor {...fieldType.editor.defaultValue}
                           on:model-change={handleModelChange}
                           model={convertGridFieldValueToEditorModel(editValue, asSupported(fieldType.editor.defaultValue.type))}
                           type={fieldType.editor.defaultValue.type}
                           size={ValueEditorSize.XS}
                           isFocused={true}/>
        </KpValueEditorForceSettings>
    </div>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .value-container {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        overflow: hidden;
        position: relative;

        &.end-align {
            justify-content: end;
            text-align: end;
            transition: padding-left 0.2s ease-in-out;
        }
    }

    .value-editor-container {
        display: flex;
        align-items: center;
        top: 0;
        left: 0;
        bottom: 0;
        width: 100%;
        position: absolute;
        background-color: #F4E1DC;
        outline: 2px solid var(--brand-orange);
        box-shadow: 0 0 3px 3px rgba(255, 87, 18, 0.2);
        min-width: 100%;
        z-index: 1;

        :global {
            kp-value-editor {
                width: 100%;

                input[type="text"],
                input[type="number"] {
                    all: unset;
                    width: 100%;
                }

                .input-group {
                    display: inline-flex;
                    width: 100%;
                }

                .input-group-btn {
                    display: contents;
                }
            }
        }
    }
</style>
